import {
  ContentChild,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Renderer2,
  OnDestroy,
  Optional,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
/**
 * Applies a class to CSS hover dropdowns when links inside of the dropdown are
 * TAB-focused, to make them visible. This makes dropdowns accessible using the
 * keyboard.
 *
 * This should be used with the `.nav-dropdown` class from `_nav-base.scss`. The
 * child `.nav-dropdown__menu` should have the ID `#dropdownMenu`.
 */
@Directive({
  selector: '[cignaHoverDropdown]',
})
export class HoverDropdownDirective implements OnDestroy {
  private _destroy$ = new Subject<void>();
  private _focus$ = new Subject<boolean>();

  constructor(private _renderer: Renderer2) {
    this._focus$
      .pipe(debounceTime(10), distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe((focused) => {
        if (focused) {
          this.open = true;

          setTimeout(() => this.alignToAvoidRightOverflow(), 0);

          return;
        }

        this.open = false;
        this.focusable = false;
      });
  }

  @ContentChild('dropdownMenu', { read: ElementRef, static: true })
  menu?: ElementRef;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  @HostBinding('class.nav-dropdown--open') open = false;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  focusable = false;

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('mouseover')
  onFocusIn() {
    this._focus$.next(true);
  }

  @HostListener('mouseout')
  onFocusOut() {
    this._focus$.next(false);
  }

  /**
   * Dropdown menus may overflow the right side of the browser window, causing
   * the window to scroll horizontally. This function detects this and moves the
   * dropdown to the left.
   */
  @HostListener('mouseenter')
  alignToAvoidRightOverflow() {
    const el = this.menu && this.menu.nativeElement;
    if (!el || !el.parentElement) {
      return;
    }

    const { left } = el.parentElement.getBoundingClientRect();
    const { width } = el.getBoundingClientRect();

    const offset = Math.min(window.innerWidth - left - width - 30, 0);
    if (offset) {
      this._renderer.setStyle(el, 'left', `${offset}px`);
    }
  }
}
